import { Provenance } from './SubstrateSearchShared';
import IconIndexType from 'owa-service/lib/contract/IconIndexType';
import ImportanceType from 'owa-service/lib/contract/ImportanceType';
import SensitivityType from 'owa-service/lib/contract/SensitivityType';

interface SubstrateSearchResponse {
    AnswerEntitySets?: any[]; // For Ignite demo (won't go beyond SDF) so not typing
    EntitySets: EntitySet[];
    Instrumentation: InstrumentationDetails;
    SearchTerms: string[];
    error?: any; // Present when call to 3S fails
}

export interface EntitySet {
    QueryAlterationResponse?: QueryAlterationResponse;
    ResultSets: SearchResultSet[];
    Type: EntitySetType;
}

export interface QueryAlterationResponse {
    AlteredQuery: {
        AlteredQuery: {
            HighlightedString: string;
            RawString: string;
            ReferenceId: string;
        };
        FlaggedTokens: [
            {
                Length: number;
                Suggestion: string;
                Offset: number;
            }
        ];
    };
    QueryAlterationType: QueryAlterationType;
    RecourseQuery: {
        HighlightedString: string;
        RawString: string;
    };
}

export type QueryAlterationType =
    | 'Suggestion'
    | 'Modification'
    | 'NoResultModification'
    | 'NoRequeryModification'
    | 'Modification'
    | 'Extension';

export interface SearchResultSet {
    MoreResultsAvailable: boolean;
    Provenance: Provenance;
    QueryId: QueryId;
    Results: SearchResult[];
    Total: number;
}

export interface QueryId {
    Id: string;
}

export interface SearchResult {
    Id: string;
    Provenance: string;
    ReferenceId: string;
    Source: {};
    InstUrlSuffix: string;
}

export interface InstrumentationDetails {
    TraceId: string;
    InstUrlBase: string;
}

export type EntitySetType =
    | 'Message'
    | 'Messages'
    | 'Conversation'
    | 'Conversations'
    | 'Event'
    | 'Events';

export interface SearchResultItem {
    Categories: string[];
    ConversationId: SearchResultItemId;
    ConversationThreadId: string;
    DateTimeCreated: string;
    DateTimeLastModified: string;
    DateTimeReceived: string;
    DateTimeSent: string;
    DiagnosticsItemContent: string;
    DiagnosticsItemQuery: string;
    DisplayBcc: string;
    DisplayCc: string;
    DisplayTo: string;
    ExtendedProperty: SearchResultExtendedPropertyType[];
    Flag: SearchResultFlagType;
    HasAttachments: boolean;
    Hashtags: string[];
    IconIndex: IconIndexType;
    Importance: ImportanceType;
    IsDraft: boolean;
    IsFromMe: boolean;
    ItemClass: string;
    ItemId: SearchResultItemId;
    ParentFolderId: SearchResultFolderId;
    Preview: string;
    SearchKey: string;
    Sensitivity: SensitivityType;
    Size: number;
    SortKey: number;
    SortOrderSource: string;
    Subject: string;
}

export interface SearchResultItemId {
    ChangeKey: string;
    EntryId: string;
    Id: string;
    OutlookItemId: string;
}

export interface SearchResultFolderId {
    ChangeKey: string;
    Id: string;
}

export interface SearchResultExtendedPropertyType {
    ExtendedFieldURI: string;
    Value: string;
    Values: string[];
}

export interface SearchResultFlagType {
    CompleteDate: string;
    DueDate: string;
    FlagStatus: SearchResultFlagStatus;
    StartDate: string;
}

export type SearchResultFlagStatus = 'NotFlagged' | 'Complete' | 'Flagged';

export interface SearchResultMessage extends SearchResultItem {
    ConversationIndex: string;
    ConversationTopic: string;
    FolderRefinerId: string;
    From: SearchResultRecipient;
    InternetMessageId: string;
    IsRead: boolean;
    MailboxGuids: string[];
    Sender: SearchResultRecipient;
    SenderSMTPAddress: string;
}

export interface SearchResultRecipient {
    EmailAddress: SearchResultEmailAddress;
}

export interface SearchResultEmailAddress {
    Address: string;
    EmailAddressIndex: string;
    IsDomainInOrganization: boolean;
    IsExternalSmtpAddress: boolean;
    ItemId: SearchResultItemId;
    MailboxType: string;
    Name: string;
    OriginalDisplayName: string;
    RelevanceScore: number;
    RoutingType: string;
    SipUri: string;
    Submitted: boolean;
}

export interface SearchResultConversation extends SearchResultItem {
    Categories: string[];
    ConversationTopic: string;
    FlagStatus: SearchResultFlagStatus;
    FolderRefinerId: string;
    From: SearchResultRecipient;
    GlobalItemIds: SearchResultItemId[];
    GlobalMessageCount: number;
    GlobalUnreadCount: number;
    HasIrm: boolean;
    ItemClasses: string[];
    ItemIds: SearchResultItemId[];
    LastDeliveryOrRenewTime: string;
    LastDeliveryTime: string;
    LastModifiedTime: string;
    MailboxGuids: string[];
    MessageCount: number;
    SenderSMTPAddress: string;
    UniqueRecipients: string[];
    UniqueSenders: string[];
    UnreadCount: number;
}

export interface SearchResultEvent extends SearchResultItem {
    CalendarItemType: SearchResultCalendarItemType;
    Charm: number;
    End: string;
    IsAllDayEvent: boolean;
    IsMeeting: boolean;
    IsMeetingPollEvent: boolean;
    LegacyFreeBusyStatus: SearchResultBusyType;
    Location: string;
    Organizer: SearchResultRecipient;
    SeriesMasterItemId: SearchResultItemId;
    Start: string;
    UID: string;
}

export enum SearchResultCalendarItemType {
    Single = 0,
    Occurrence = 1,
    Exception = 2,
    RecurringMaster = 3,
}

export enum SearchResultBusyType {
    Free = 0,
    Tentative = 1,
    Busy = 2,
    Oof = 3,
    WorkingElsewhere = 4,
    NoData = 5,
}

export default SubstrateSearchResponse;
